// improve-floater.tsx — the cross-surface "Improve" floater
// (docs/suggestions-bounties-prd.md). Mounted once, globally, above the router.
// It detects the current identity from the URL + token, lets any of the five
// identities submit a surface-typed suggestion, and shows their bounty balance,
// ledger, and the open redemption offers they can spend points on.
//
// Styling is semantic-token-only (registered in check-contrast RULE3_FILES);
// brand orange (#FF5C00 / #E55200) is the sole hex, per the project contract.
// The floater inherits the current surface's light/dark mode via useThemeMode
// so it always matches the portal it floats over.

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  Lightbulb, X, Send, Award, Gift, Loader2, CheckCircle2, AlertCircle,
  TrendingUp, History, Sparkles,
} from "lucide-react";
import ProgressLoader from "./progress-loader";
import { useThemeMode } from "@/lib/use-theme-mode";
import {
  detectSurface, themeScopeFor, SUB_CATEGORIES,
  submitSuggestion, getMyBounties, getRedemptionOffers, redeemOffer, getMyRedemptions, getMySuggestions,
  type SurfaceConfig, type SubCategory,
  type BountySummary, type LedgerEntry, type RedemptionOffer, type RedemptionRequest, type Suggestion,
} from "@/lib/improvements-client";

type Tab = "suggest" | "bounties" | "rewards";

const SUBCAT_LABEL: Record<SubCategory, string> = {
  bug: "Bug", ux: "UX / polish", feature: "Feature idea", content: "Content / copy",
  performance: "Performance", trust_safety: "Trust & safety", other: "Other",
};

function isFloaterDisabled(): boolean {
  try { return localStorage.getItem("improve_floater_off") === "true"; } catch { return false; }
}

export default function ImproveFloater() {
  // Re-detect the surface on every navigation. The floater is mounted inside
  // the WouterRouter, so useLocation() reacts to EVERY navigation method —
  // pushState, replaceState (redirects / navigate(..., { replace: true })),
  // and popstate. The previous manual history.pushState patch missed
  // replaceState, leaving a stale path after auth redirects; the floater then
  // sat in the default corner slot and covered the Kavya chat FAB.
  const [path] = useLocation();

  const surface = useMemo(() => detectSurface(path), [path]);
  // The investor portal mounts a Kavya chat FAB at bottom-right. Stack the
  // Improve floater ABOVE it there so the two never overlap (they used to
  // collide and the pill hid the chat button).
  const hasChatFab = useMemo(() => /^\/investor\/portal(?:\/|$)/.test(path), [path]);
  // Research reading surfaces need an unobstructed document and toolbar.
  // Feedback remains available on the research library and other portal pages.
  const isResearchReading = /^\/investor\/portal\/research(?:\/|$)/.test(path);
  if (!surface || isFloaterDisabled() || isResearchReading) return null;
  return <FloaterInner surface={surface} hasChatFab={hasChatFab} />;
}

function FloaterInner({ surface, hasChatFab }: { surface: SurfaceConfig; hasChatFab: boolean }) {
  const { mode } = useThemeMode(themeScopeFor(surface.kind), "dark");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("suggest");

  // When a chat FAB shares the corner, raise the floater to clear it (chat FAB
  // is bottom-6 + 56px tall). Otherwise sit in the default bottom-right slot.
  const posClass = hasChatFab
    ? "fixed bottom-24 right-6 z-[9999] font-sans"
    : "fixed bottom-5 right-5 z-[9999] font-sans";

  return (
    <div data-improve-theme={mode} className={posClass} {...{ [`data-${themeScopeFor(surface.kind)}-theme`]: mode }}>
      {open ? (
        <div className="w-[min(92vw,420px)] max-h-[80vh] flex flex-col rounded-2xl border border-border bg-card text-card-foreground shadow-2xl overflow-hidden">
          <Header surface={surface} onClose={() => setOpen(false)} tab={tab} setTab={setTab} />
          <div className="flex-1 overflow-y-auto p-4">
            {tab === "suggest" && <SuggestPanel surface={surface} />}
            {tab === "bounties" && <BountiesPanel surface={surface} />}
            {tab === "rewards" && <RewardsPanel surface={surface} />}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full bg-[#FF5C00] hover:bg-[#E55200] text-white pl-3.5 pr-4 py-3 shadow-lg transition-colors"
          aria-label="Open improvement suggestions"
        >
          <Lightbulb className="w-5 h-5" />
          <span className="text-sm font-semibold">Improve</span>
        </button>
      )}
    </div>
  );
}

function Header({ surface, onClose, tab, setTab }: { surface: SurfaceConfig; onClose: () => void; tab: Tab; setTab: (t: Tab) => void }) {
  return (
    <div className="border-b border-border">
      <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
        <Sparkles className="w-4 h-4 text-[#FF5C00]" />
        <div className="leading-tight">
          <p className="text-sm font-semibold text-foreground">Improvement Engine</p>
          <p className="text-[11px] text-muted-foreground">Suggesting as {surface.label}</p>
        </div>
        <button onClick={onClose} className="ml-auto p-1 rounded-md hover:bg-muted text-muted-foreground" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-1 px-2">
        <Tab label="Suggest" icon={<Send className="w-3.5 h-3.5" />} active={tab === "suggest"} onClick={() => setTab("suggest")} />
        <Tab label="My Bounties" icon={<Award className="w-3.5 h-3.5" />} active={tab === "bounties"} onClick={() => setTab("bounties")} />
        <Tab label="Rewards" icon={<Gift className="w-3.5 h-3.5" />} active={tab === "rewards"} onClick={() => setTab("rewards")} />
      </div>
    </div>
  );
}

function Tab({ label, icon, active, onClick }: { label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${active ? "text-foreground border-[#FF5C00]" : "text-muted-foreground hover:text-foreground border-transparent"}`}
    >
      {icon}{label}
    </button>
  );
}

// ---- Suggest -------------------------------------------------------------

function SuggestPanel({ surface }: { surface: SurfaceConfig }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [subCategory, setSubCategory] = useState<SubCategory>("feature");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = title.trim().length >= 3 && body.trim().length >= 3 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError(null);
    const res = await submitSuggestion(surface, {
      title: title.trim(),
      bodyMd: body.trim(),
      subCategory,
      type: surface.defaultType,
      surfacePath: window.location.pathname,
    });
    setBusy(false);
    if (res.ok) { setDone(true); setTitle(""); setBody(""); setSubCategory("feature"); }
    else setError(res.error || "Could not submit. Please try again.");
  };

  if (done) {
    return (
      <div className="text-center py-8 space-y-3">
        <CheckCircle2 className="w-10 h-10 text-[#FF5C00] mx-auto" />
        <p className="text-sm font-semibold text-foreground">Thanks — your suggestion is in.</p>
        <p className="text-xs text-muted-foreground">If it's accepted, you'll earn bounty points and an email. Track it under <span className="font-medium text-foreground">My Bounties</span>.</p>
        <button onClick={() => setDone(false)} className="text-xs font-medium text-[#FF5C00] hover:underline">Submit another</button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Spotted something to fix or improve? Tell us — accepted ideas earn bounty points.</p>
      <div>
        <label className="block text-[11px] font-medium text-muted-foreground mb-1">Category</label>
        <select
          value={subCategory}
          onChange={(e) => setSubCategory(e.target.value as SubCategory)}
          className="w-full rounded-lg border border-border bg-background text-foreground text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40"
        >
          {SUB_CATEGORIES.map((c) => <option key={c} value={c}>{SUBCAT_LABEL[c]}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[11px] font-medium text-muted-foreground mb-1">Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="One line — what's the idea?"
          className="w-full rounded-lg border border-border bg-background text-foreground text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40"
        />
      </div>
      <div>
        <label className="block text-[11px] font-medium text-muted-foreground mb-1">Details</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={5000}
          rows={5}
          placeholder="What's happening now, and what would be better? Steps to reproduce if it's a bug."
          className="w-full rounded-lg border border-border bg-background text-foreground text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#FF5C00]/40"
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span>
        </div>
      )}

      {busy ? (
        <ProgressLoader active title="Submitting your suggestion" stages={["Validating your input", "Saving to the review queue", "Notifying the product team"]} stageIntervalMs={1200} />
      ) : (
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white px-4 py-2.5 text-sm font-semibold transition-colors"
        >
          <Send className="w-4 h-4" /> Submit suggestion
        </button>
      )}
    </div>
  );
}

// ---- My Bounties ---------------------------------------------------------

function BountiesPanel({ surface }: { surface: SurfaceConfig }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<BountySummary | null>(null);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [redemptions, setRedemptions] = useState<RedemptionRequest[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      const [b, s, r] = await Promise.all([getMyBounties(surface), getMySuggestions(surface), getMyRedemptions(surface)]);
      if (!alive) return;
      if (b.ok && b.data) { setSummary(b.data.summary); setLedger(b.data.ledger); }
      else setError(b.error || "Could not load your bounties.");
      if (s.ok && s.data) setSuggestions(s.data.suggestions);
      if (r.ok && r.data) setRedemptions(r.data.requests);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [surface]);

  if (loading) {
    return <ProgressLoader active title="Loading your bounties" stages={["Tallying your ledger", "Fetching your suggestions", "Checking redemptions"]} stageIntervalMs={900} />;
  }
  if (error) {
    return <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span></div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Balance" value={summary?.balance ?? 0} icon={<Award className="w-3.5 h-3.5 text-[#FF5C00]" />} />
        <Stat label="Lifetime" value={summary?.lifetimeEarned ?? 0} icon={<TrendingUp className="w-3.5 h-3.5 text-[#FF5C00]" />} />
        <Stat label="Rank" value={summary?.rank ? `#${summary.rank}` : "—"} icon={<Sparkles className="w-3.5 h-3.5 text-[#FF5C00]" />} />
      </div>

      <Section title="My suggestions">
        {suggestions.length === 0 ? <Empty text="No suggestions yet — share one from the Suggest tab." /> : (
          <ul className="space-y-1.5">
            {suggestions.slice(0, 12).map((s) => (
              <li key={s.id} className="rounded-lg border border-border bg-background p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-foreground truncate">{s.title}</p>
                  <StatusPill status={s.status} />
                </div>
                {s.reviewNote && <p className="text-[11px] text-muted-foreground mt-1">{s.reviewNote}</p>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Points history" icon={<History className="w-3.5 h-3.5 text-muted-foreground" />}>
        {ledger.length === 0 ? <Empty text="No points yet. Accepted suggestions earn points." /> : (
          <ul className="space-y-1">
            {ledger.slice(0, 20).map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-2 text-xs py-1">
                <span className="text-muted-foreground truncate">{l.note || l.reason}</span>
                <span className={`font-mono font-semibold shrink-0 ${l.delta >= 0 ? "text-[#FF5C00]" : "text-muted-foreground"}`}>{l.delta >= 0 ? "+" : ""}{l.delta}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {redemptions.length > 0 && (
        <Section title="My redemptions">
          <ul className="space-y-1.5">
            {redemptions.slice(0, 10).map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background p-2.5">
                <p className="text-xs font-medium text-foreground truncate">{r.offerTitle || `Offer #${r.offerId}`}</p>
                <StatusPill status={r.status} />
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

// ---- Rewards -------------------------------------------------------------

function RewardsPanel({ surface }: { surface: SurfaceConfig }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offers, setOffers] = useState<RedemptionOffer[]>([]);
  const [balance, setBalance] = useState(0);
  const [redeemingId, setRedeemingId] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    const res = await getRedemptionOffers(surface);
    if (res.ok && res.data) { setOffers(res.data.offers); setBalance(res.data.balance); }
    else setError(res.error || "Could not load rewards.");
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [surface]);

  const redeem = async (offer: RedemptionOffer) => {
    setRedeemingId(offer.id); setNotice(null);
    const res = await redeemOffer(surface, offer.id);
    setRedeemingId(null);
    if (res.ok && res.data) {
      setBalance(res.data.balance);
      setNotice({ kind: "ok", text: `Redeemed "${offer.title}". The team will follow up to fulfil it.` });
      await load();
    } else {
      setNotice({ kind: "err", text: res.error || "Redemption failed." });
    }
  };

  if (loading) return <ProgressLoader active title="Loading rewards" stages={["Fetching open offers", "Checking your balance"]} stageIntervalMs={900} />;
  if (error) return <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-lg border border-[#FF5C00]/30 bg-[#FF5C00]/5 px-3 py-2">
        <span className="text-xs text-muted-foreground">Your balance</span>
        <span className="text-sm font-bold text-foreground">{balance} pts</span>
      </div>

      {notice && (
        <div className={`flex items-start gap-2 rounded-lg p-2.5 text-xs ${notice.kind === "ok" ? "border border-[#FF5C00]/30 bg-[#FF5C00]/5 text-foreground" : "border border-destructive/30 bg-destructive/10 text-destructive"}`}>
          {notice.kind === "ok" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-[#FF5C00]" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
          <span>{notice.text}</span>
        </div>
      )}

      {offers.length === 0 ? <Empty text="No rewards are open right now. Check back soon." /> : (
        <ul className="space-y-2">
          {offers.map((o) => {
            const affordable = balance >= o.pointCost;
            const isRedeeming = redeemingId === o.id;
            return (
              <li key={o.id} className="rounded-xl border border-border bg-background p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{o.title}</p>
                    {o.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{o.description}</p>}
                    {o.stock !== null && <p className="text-[11px] text-muted-foreground mt-1">{o.stock} left</p>}
                  </div>
                  <span className="text-xs font-bold text-[#FF5C00] shrink-0">{o.pointCost} pts</span>
                </div>
                {isRedeeming ? (
                  <div className="mt-2"><ProgressLoader active title="Redeeming" stages={["Reserving the reward", "Debiting your points"]} stageIntervalMs={900} /></div>
                ) : (
                  <button
                    onClick={() => redeem(o)}
                    disabled={!affordable || redeemingId !== null}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 rounded-lg bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white px-3 py-2 text-xs font-semibold transition-colors"
                  >
                    {affordable ? <><Gift className="w-3.5 h-3.5" /> Redeem</> : `Need ${o.pointCost - balance} more pts`}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---- small shared bits ---------------------------------------------------

function Stat({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-background p-2.5 text-center">
      <div className="flex items-center justify-center gap-1">{icon}</div>
      <p className="text-lg font-bold text-foreground mt-0.5 tabular-nums">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{icon}{title}</p>
      {children}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-xs text-muted-foreground italic py-2">{text}</p>;
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === "accepted" || status === "fulfilled" ? "border-[#FF5C00]/40 text-[#FF5C00] bg-[#FF5C00]/10"
    : status === "declined" || status === "rejected" ? "border-border text-muted-foreground bg-muted"
    : "border-border text-foreground bg-muted";
  return <span className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border shrink-0 ${tone}`}>{status.replace(/_/g, " ")}</span>;
}
